import { useState, useEffect } from "react";
import { 
  ArrowUp, 
  Lightbulb, 
  BookOpen, 
  Users, 
  Briefcase, 
  Building, 
  MessageSquare,
  ChevronRight,
  Menu,
  Home
} from "lucide-react";
import { cn } from "@/lib/utils";
import CareerCoachBot from "../chat/CareerCoachBot";

interface NavItem {
  id: string;
  icon: React.ReactNode;
  label: string;
}

export default function QuickNav() {
  const [activeSection, setActiveSection] = useState("");
  const [isVisible, setIsVisible] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isChatOpen, setIsChatOpen] = useState(false);
  
  // Define sections to navigate to
  const navItems: NavItem[] = [
    { id: "top", icon: <Home className="h-4 w-4 lg:h-5 lg:w-5" />, label: "Home" },
    { id: "how-it-works", icon: <Lightbulb className="h-4 w-4 lg:h-5 lg:w-5" />, label: "Framework" },
    { id: "business-impact", icon: <Briefcase className="h-4 w-4 lg:h-5 lg:w-5" />, label: "Business Impact" },
    { id: "success-stories", icon: <Users className="h-4 w-4 lg:h-5 lg:w-5" />, label: "Success Stories" },
    { id: "call-to-action", icon: <MessageSquare className="h-4 w-4 lg:h-5 lg:w-5" />, label: "White Paper" },
    { id: "programs", icon: <BookOpen className="h-4 w-4 lg:h-5 lg:w-5" />, label: "Programs" },
    { id: "get-involved", icon: <Building className="h-4 w-4 lg:h-5 lg:w-5" />, label: "Get Involved" }
  ];

  // Show QuickNav after scrolling
  useEffect(() => {
    const handleScroll = () => {
      // Always visible for now
      setIsVisible(true);
      
      // Determine which section is currently visible
      navItems.forEach(item => {
        if (item.id === "top") return; // Skip the top item
        const element = document.getElementById(item.id);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top < window.innerHeight * 0.5 && rect.bottom > 0) {
            setActiveSection(item.id);
          }
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Scroll to section
  const scrollToSection = (id: string) => {
    if (id === "top") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      const element = document.getElementById(id);
      if (element) {
        // Scroll to element with offset for the header
        const headerOffset = 100;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth"
        });
      }
    }
  };

  // Toggle collapsed state
  const toggleCollapsed = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Toggle chat window
  const toggleChat = () => {
    setIsChatOpen(!isChatOpen);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed z-40 right-4 bottom-20 flex flex-col gap-4 items-end">
      {/* Career Coach Bot Button */}
      <button 
        onClick={toggleChat}
        className="flex flex-col items-center justify-center w-12 h-12 lg:w-14 lg:h-14 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-full shadow-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 border-2 border-white"
        aria-label="Open Career Coach Chat"
        title="Talk to Career Coach AI"
      >
        <div className="relative">
          <MessageSquare className="h-5 w-5 lg:h-6 lg:w-6" />
          <span className="absolute -top-1.5 -right-1.5 h-3 w-3 bg-green-500 rounded-full animate-pulse"></span>
        </div>
        <span className="text-[9px] lg:text-[10px] font-bold mt-0.5">COACH</span>
      </button>
      
      {/* Career Coach Chat Window */}
      <CareerCoachBot isOpen={isChatOpen} onToggle={toggleChat} />

      {/* Navigation Bar */}
      <div className="flex items-center">
        {/* Navigation buttons */}
        <div 
          className={cn(
            "bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg mr-2 transition-all duration-300 overflow-hidden border border-gray-100 max-h-[500px]",
            isCollapsed ? "max-w-0 max-h-0 opacity-0" : "max-w-[250px] sm:max-w-[450px] md:max-w-[700px] lg:max-w-[800px] opacity-100"
          )}
        >
          <nav className="p-2">
            <div className="flex flex-wrap gap-1 justify-center">
              {navItems.map((item) => (
                <div key={item.id} className="w-[60px] lg:w-[80px]">
                  <button
                    onClick={() => scrollToSection(item.id)}
                    className={cn(
                      "flex flex-col items-center justify-center w-full p-1 lg:p-2 hover:bg-primary-50 transition-colors rounded-lg",
                      activeSection === item.id 
                        ? "text-primary-700 bg-primary-50 ring-1 ring-primary-200" 
                        : "text-slate-600"
                    )}
                    aria-label={`Scroll to ${item.label}`}
                    title={item.label}
                  >
                    {item.icon}
                    <span className="text-[8px] lg:text-[10px] mt-1 truncate w-full text-center font-medium">{item.label}</span>
                  </button>
                </div>
              ))}
              
              {/* To Top button */}
              <div className="w-[60px]">
                <button
                  onClick={() => scrollToSection("top")}
                  className="flex flex-col items-center justify-center w-full p-1 text-primary-600 hover:bg-primary-50 transition-colors rounded-lg"
                  aria-label="Scroll to top"
                  title="Scroll to top"
                >
                  <ArrowUp className="h-4 w-4" />
                  <span className="text-[8px] mt-1 truncate w-full text-center">Top</span>
                </button>
              </div>
            </div>
          </nav>
        </div>
        
        {/* Toggle button */}
        <button
          onClick={toggleCollapsed}
          className="flex flex-col items-center justify-center w-12 h-12 lg:w-14 lg:h-14 bg-gradient-to-r from-purple-600 to-indigo-700 text-white rounded-full shadow-lg hover:from-purple-700 hover:to-indigo-800 transition-all duration-300 transform hover:scale-105 border-2 border-white"
          aria-label={isCollapsed ? "Expand navigation" : "Collapse navigation"}
        >
          {isCollapsed ? (
            <>
              <Menu className="h-5 w-5 lg:h-6 lg:w-6" />
              <span className="text-[9px] lg:text-[10px] font-bold mt-0.5">MENU</span>
            </>
          ) : (
            <>
              <ChevronRight className="h-5 w-5 lg:h-6 lg:w-6" />
              <span className="text-[9px] lg:text-[10px] font-bold mt-0.5">CLOSE</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}